import { useMemo } from "react";

import type { Messages } from "@eroad/types";

import { useIntl } from "../../hooks/useIntl";
import { IntlProvider } from "../IntlProvider";
import type { GetMessagesFromLocaleParams, NestedIntlProviderProps } from "./types";

/**
 * A nested IntlProvider that merges root i18n config with given messages.
 * Use to combine app and package translations.
 */
const NestedIntlProvider = ({ children, enAU, enNZ, enUS, frCA }: NestedIntlProviderProps): JSX.Element => {
  const { locale, messages, timeZone } = useIntl();

  // this is memoized to avoid unnecessary re-renders when locale or all messages change
  const mergedMessages = useMemo(
    () => Object.assign({}, messages, getMessagesFromLocale({ enAU, enNZ, enUS, frCA, locale })),
    [messages, locale, enNZ, enAU, enUS, frCA],
  );

  return (
    <IntlProvider locale={locale} messages={mergedMessages} timeZone={timeZone}>
      {children}
    </IntlProvider>
  );
};

/**
 * @internal
 */
function getMessagesFromLocale({ enAU, enNZ, enUS, frCA, locale }: GetMessagesFromLocaleParams): Messages {
  if (locale === "en-AU") return { ...enNZ, ...enAU };
  if (locale === "en-US") return { ...enNZ, ...enUS };
  if (locale === "fr-CA") return { ...enNZ, ...frCA };
  return enNZ;
}

export { NestedIntlProvider };
